import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { UserContext } from '../../UserContext';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';
import { usePreventDoubleClick } from '../hooks/usePreventDoubleClick';

import styles from './AnaliseRateio.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

function AnaliseRateio() {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const { get, post } = useHttpClient();
    const { exibirErro, exibirSucesso } = useNotificacoes();

    const hoje = new Date();
    const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;

    const [usinas, setUsinas] = useState([]);
    const [filtros, setFiltros] = useState({
        idUsina: '',
        mes: mesAtual
    });
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(false);

    const buscou = useRef(false);
    const carregouViaUrl = useRef(false);

    useEffect(() => {
        if (user === null) {
            navigate('/login');
        }
    }, [user, navigate]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const idUsinaUrl = params.get('idUsina');
        const mesUrl = params.get('mes');

        if (idUsinaUrl || mesUrl) {
            setFiltros((prev) => ({
                ...prev,
                idUsina: idUsinaUrl || prev.idUsina,
                mes: mesUrl || prev.mes
            }));
        }
    }, [location.search]);

    useEffect(() => {
        if (!user?.empresa?.id) return;

        let ativo = true;

        const carregarUsinas = async () => {
            try {
                const resp = await get(`/hello-sol/usinas/buscar?idEmpresa=${user.empresa.id}`);
                if (!ativo) return;
                setUsinas(Array.isArray(resp) ? resp : (resp.content ?? []));
            } catch (err) {
                if (ativo) {
                    exibirErro(err.message || 'Erro ao buscar usinas.');
                }
            }
        };

        carregarUsinas();

        return () => {
            ativo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.empresa?.id]);

    const fetchAnalise = async () => {
        if (!filtros.idUsina) {
            exibirErro('Selecione a usina.');
            return;
        }

        if (!filtros.mes) {
            exibirErro('Selecione o mês.');
            return;
        }

        setLoading(true);

        try {
            const resp = await get(
                `/hello-sol/rateios/analise?idUsina=${filtros.idUsina}&competencia=${filtros.mes}`
            );

            setDados(resp);
            buscou.current = true;
        } catch (err) {
            setDados(null);
            buscou.current = true;
            exibirErro(err.message || 'Erro ao analisar rateio.');
        } finally {
            setLoading(false);
        }
    };

    const [safeFetch] = usePreventDoubleClick(fetchAnalise);

    useEffect(() => {
        if (!filtros.idUsina || !filtros.mes) return;
        if (!usinas.length) return;
        if (carregouViaUrl.current) return;

        const existeUsina = usinas.some((u) => String(u.id) === String(filtros.idUsina));
        if (!existeUsina) return;

        carregouViaUrl.current = true;
        fetchAnalise();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtros.idUsina, filtros.mes, usinas]);

    const handleSubmit = (e) => {
        e.preventDefault();
        safeFetch();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFiltros((prev) => ({ ...prev, [name]: value }));
        if (name === 'idUsina' || name === 'mes') {
            carregouViaUrl.current = false;
        }
    };

    const handleSalvarProposta = async () => {
        if (!dados?.unidades?.length) {
            exibirErro('Faça a análise antes de salvar a proposta.');
            return;
        }

        const itens = dados.unidades
            .filter((u) => u.idRateio && u.percentualSugerido != null)
            .map((u) => ({
                idRateio: u.idRateio,
                percentual: u.percentualSugerido
            }));

        if (!itens.length) {
            exibirErro('Nenhum percentual sugerido disponível para salvar.');
            return;
        }

        setLoading(true);

        try {
            await post('/hello-sol/rateios/analise/aplicar-sugestao', { itens });
            exibirSucesso('Proposta de rateio salva no Hello Sol com sucesso.');
            await fetchAnalise();
        } catch (err) {
            exibirErro(err.message || 'Erro ao salvar a proposta.');
        } finally {
            setLoading(false);
        }
    };

    const handleExportarXls = async () => {
        if (!filtros.idUsina) {
            exibirErro('Selecione a usina.');
            return;
        }

        if (!filtros.mes) {
            exibirErro('Selecione o mês.');
            return;
        }

        try {
            const apiBase =
                process.env.REACT_APP_API_URL ||
                process.env.REACT_APP_BASE_URL ||
                'http://localhost:8080';

            const token = localStorage.getItem('token') || '';

            const response = await fetch(
                `${apiBase}/hello-sol/rateios/analise/xls?idUsina=${filtros.idUsina}&competencia=${filtros.mes}`,
                {
                    method: 'GET',
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    }
                }
            );

            const contentType = response.headers.get('content-type') || '';

            if (!response.ok) {
                const erroTexto = await response.text();
                throw new Error(erroTexto || 'Erro ao exportar XLS.');
            }

            if (!contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
                const texto = await response.text();
                throw new Error(texto || 'A resposta recebida não é um arquivo XLSX válido.');
            }

            const blob = await response.blob();

            if (!blob || blob.size === 0) {
                throw new Error('O arquivo XLSX foi gerado vazio.');
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `analise-rateio-${filtros.mes}.xlsx`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            exibirErro(err.message || 'Erro ao exportar XLS.');
        }
    };

    const formatNumber = (valor, scale = 2) =>
        Number(valor || 0).toLocaleString('pt-BR', {
            minimumFractionDigits: scale,
            maximumFractionDigits: scale
        });

    const getStatusClass = (status) => {
        if (status === 'OK') return styles.statusOk;
        if (status === 'RECEBEU_MAIS') return styles.statusMais;
        if (status === 'RECEBEU_MENOS') return styles.statusMenos;
        return styles.statusDefault;
    };

    const getSaldoClass = (valor) => {
        const numero = Number(valor || 0);
        if (numero > 0) return styles.valorPositivo;
        if (numero < 0) return styles.valorNegativo;
        return styles.valorNeutro;
    };

    const unidades = dados?.unidades ?? [];
    const insights = dados?.insights ?? [];

    const consumoRealTotal = unidades.reduce((acc, u) => acc + Number(u.energiaRecebida || 0), 0);
    const unidadesComProblema = unidades.filter((u) => u.status !== 'OK').length;

    const resumoOperacional = dados
        ? `${unidadesComProblema} de ${unidades.length} unidades estão fora do equilíbrio ideal no cenário atual.`
        : null;

    return (
        <div className={styles_global.container}>
            <h2>Análise de Rateio</h2>

            <form className={styles.filterForm} onSubmit={handleSubmit}>
                <div className={`${styles_content.formField} ${styles.usinaField}`}>
                    <label>Usina</label>
                    <select name="idUsina" value={filtros.idUsina} onChange={handleChange}>
                        <option value="">Selecione a Usina</option>
                        {usinas.map((u) => (
                            <option key={u.id} value={u.id}>
                                {u.nome}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={`${styles_content.formField} ${styles.mesField}`}>
                    <label>Mês</label>
                    <input type="month" name="mes" value={filtros.mes} onChange={handleChange} />
                </div>

                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>

                    {/*
                    <button type="button" onClick={handleSalvarProposta} disabled={loading}>
                        Salvar Proposta
                    </button>
                    */}


                    <button
                        type="button"
                        onClick={() =>
                            navigate(`/home/rateio-dashboard?idUsina=${filtros.idUsina}&mes=${filtros.mes}`)
                        }
                        disabled={!filtros.idUsina}
                    >
                        Dashboard
                    </button>
                </div>
            </form>

            {/*
            <div className={styles.avisoImportante}>
                <strong>Importante:</strong> a proposta é salva no Hello Sol como apoio à tomada de decisão.
                Para produzir efeito real na compensação de energia, o rateio também deve ser alterado na distribuidora.
            </div>
            */}

            {loading && <p className={styles_global.loading}>Carregando...</p>}

            {!loading && dados && (
                <>
                    {resumoOperacional && (
                        <div className={styles.resumoOperacionalBox}>
                            <h3>Resumo Operacional</h3>
                            <p>{resumoOperacional}</p>
                        </div>
                    )}

                    <div className={styles.kpiGrid}>
                        <div className={styles.card}>
                            <h4>Energia Produzida</h4>
                            <p>{formatNumber(dados.energiaProduzida)} kWh</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Consumo Real</h4>
                            <p>{formatNumber(consumoRealTotal)} kWh</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Sobra Real</h4>
                            <p>{formatNumber(dados.energiaSobra)} kWh</p>
                        </div>

                        <div className={`${styles.card} ${styles.cardDestaque}`}>
                            <h4>Eficiência</h4>
                            <p>{formatNumber(dados.percentualEficiencia)}%</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Unidades com Problema</h4>
                            <p>{unidadesComProblema}</p>
                        </div>
                    </div>

                    <div className={styles.acoesTabela}>
                        <button
                            type="button"
                            className={styles.botaoSecundario}
                            onClick={handleExportarXls}
                            disabled={!filtros.idUsina || loading}
                        >
                            Exportar Análise Completa
                        </button>
                    </div>

                    {dados.mensagemAlertaCritico && (
                        <div className={styles.alertaCritico}>
                            {dados.mensagemAlertaCritico}
                        </div>
                    )}

                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                            <tr>
                                <th>UNIDADE</th>
                                <th>% ATUAL</th>
                                <th>% SUGERIDO</th>
                                <th>CONSUMO UC</th>
                                <th>CRÉDITOS RECEBIDOS</th>
                                <th>SALDO</th>
                                <th>STATUS ATUAL</th>
                            </tr>
                            </thead>
                            <tbody>
                            {unidades.map((u) => (
                                <tr key={`${u.idRateio || ''}-${u.unidadeId}`}>
                                    <td>{u.nomeUnidade}</td>
                                    <td>{formatNumber(u.percentual, 4)}%</td>
                                    <td>{formatNumber(u.percentualSugerido, 4)}%</td>
                                    <td>{formatNumber(u.energiaRecebida)} kWh</td>
                                    <td>{formatNumber(u.creditoRecebido)} kWh</td>
                                    <td className={getSaldoClass(u.saldoAtual)}>
                                        {formatNumber(u.saldoAtual)} kWh
                                    </td>
                                    <td>
                                            <span className={`${styles.statusBadge} ${getStatusClass(u.status)}`}>
                                                {u.status}
                                            </span>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {insights.length > 0 && (
                        <div className={styles.insightsBox}>
                            <h3>Insights Operacionais</h3>
                            <ul>
                                {insights.map((i, idx) => (
                                    <li key={idx}>{i.mensagem}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}

            {!loading && buscou.current && !dados && (
                <p className={styles_global.noData}>Nenhum dado encontrado.</p>
            )}

            {!loading && buscou.current && dados && unidades.length === 0 && (
                <p className={styles_global.noData}>Nenhuma unidade encontrada para os filtros selecionados.</p>
            )}
        </div>
    );
}

export default AnaliseRateio;